import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `TypeChip` (P0-9 deliverable 10): the link to a canon document. It is a
 * `<button>`, never an anchor, so Mod-click opens no browser tab and
 * Alt-click downloads nothing. The glyph is drawn in the type color and the
 * label in `text`; the accessible name is "<Type>: <Title>". Hovering or
 * focusing it for 300 ms shows a preview.
 *
 * Open modes follow the baseline keymap: click (or Enter / Space) opens a
 * side peek, Mod-click (Mod-Enter) the full page, Alt-click (Alt-Enter) a
 * center peek and Shift-click (Shift-Enter) a split pane.
 *
 * Whenever the chip is tinted with its type color (hover, focus, selected)
 * it carries `data-tint="type"`; the tint is at most 12 % and its label never
 * turns `textMuted`. A type with no hue of its own in the current direction
 * (`note`, and a user type such as Deity outside Grimoire Noir) is never
 * tinted: it carries `data-neutral` and takes a neutral wash instead.
 */
import { useState } from 'react';
import { TYPE_LABELS } from '../glyphs/paths.js';
import { TypeGlyph } from '../glyphs/type-glyph.js';
import { directionById } from '../tokens/index.js';
import { typeHasHue } from '../tokens/types.js';
import { cx, hasMod, useDirection } from './infra.js';
import { HoverCard } from './overlays.js';
import { SrdBadge } from './primitives.js';
/** The open mode for a pointer or keyboard event, from its modifier keys. */
export function openModeFor(event) {
    if (hasMod(event))
        return 'page';
    if (event.altKey)
        return 'center';
    if (event.shiftKey)
        return 'split';
    return 'peek';
}
export function TypeChip({ type, title, typeLabel, srd, selected, onOpen, preview, size = 'md', className, ref, }) {
    const [hot, setHot] = useState(false);
    const name = `${typeLabel ?? TYPE_LABELS[type]}: ${title}`;
    // `note`, and a user type on the neutral treatment in this direction, is never tinted.
    const hued = typeHasHue(directionById(useDirection()), type);
    const tinted = (hot || !!selected) && hued;
    const onClick = (event) => {
        event.preventDefault();
        onOpen?.(openModeFor(event), event.currentTarget);
    };
    const onKeyDown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ')
            return;
        event.preventDefault();
        onOpen?.(openModeFor(event), event.currentTarget);
    };
    const chip = (_jsxs("button", { ref: ref, type: "button", className: cx('cx-chip', `cx-chip--${size}`, className), "aria-label": name, "data-type": type, "data-neutral": hued ? undefined : '', "data-selected": selected ? '' : undefined, "data-tint": tinted ? 'type' : undefined, style: { ['--chip-color']: `var(--cx-type-${type})` }, onClick: onClick, onKeyDown: onKeyDown, onPointerEnter: () => setHot(true), onPointerLeave: () => setHot(false), onFocus: () => setHot(true), onBlur: () => setHot(false), children: [_jsx(TypeGlyph, { type: type, size: size === 'sm' ? 14 : 16 }), _jsx("span", { className: "cx-chip__label", children: title }), srd && _jsx(SrdBadge, {})] }));
    if (preview === false || preview === undefined)
        return chip;
    return (_jsx(HoverCard, { trigger: chip, delay: 300, children: _jsxs("div", { className: "cx-chip-preview", children: [_jsxs("div", { className: "cx-chip-preview__head", children: [_jsx(TypeGlyph, { type: type, size: 16 }), _jsx("span", { className: "cx-chip-preview__type", children: typeLabel ?? TYPE_LABELS[type] }), srd && _jsx(SrdBadge, {})] }), _jsx("div", { className: "cx-chip-preview__title", children: title }), _jsx("div", { className: "cx-chip-preview__body", children: preview })] }) }));
}
//# sourceMappingURL=type-chip.js.map